import React, { useMemo, useEffect, useContext } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, SelectChangeEvent } from '@mui/material';
import { translate as t } from 'hooks/useTranslations';
import { TakeoverLayout } from '_lex/_layouts/TakeoverLayout';
import { TabItem } from '_lex/_atoms/Tabs';
import Tabs from '_lex/_atoms/Tabs';
import { useUrlStates } from 'hooks/useUrlState';
import useRiskPoolOrganizationMetrics from '../../_data_sources/useRiskPoolOrganizationMetrics';
import useRiskPoolOrganizations from '../../_data_sources/useRiskPoolOrganizations';
import Loading from 'views/App/Portal/Loading';
import Policy from './Policy';
import PerformanceReporting from './PerformanceReporting';
import { PerformanceReportingProvider } from './PerformanceReporting/PerformanceReportingContext';
import { TimeRangeFilter } from './Policy/_types';
import { useRiskPoolDemoData } from '../../_data_sources/useRiskPoolDemoData';
import { useRiskPoolPerformanceReportingDemo } from '../../_data_sources/useRiskPoolPerformanceReportingDemo';
import { getPersistentOrganizations } from '../../_data_sources/riskPoolMetricsGenerator';
import { RiskPoolOrgProvider, useRiskPoolOrgContext } from './RiskPoolOrgContext';
import Filters from './Filters';
import { AppContext } from 'views/App/context';

function RiskPoolOrganizationDetailContent() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const startDate = searchParams.get('startDate');
  const endDate = searchParams.get('endDate');
  const urlTimeRange = (searchParams.get('timeRange') as TimeRangeFilter) || TimeRangeFilter.PAST_12_MONTHS;
  const { currentOrganizationId } = useContext(AppContext);

  // Determine initial time range
  const initialTimeRange = urlTimeRange || TimeRangeFilter.PAST_12_MONTHS;

  // Get organization info - use queryParams from context if available, otherwise from URL
  const queryParams = {
    ...(startDate && { startDate }),
    ...(endDate && { endDate }),
  };
  const { data: organization, isLoading: isOrgLoading } = useRiskPoolOrganizationMetrics(orgId!, queryParams);
  
  // Check demo data feature flags
  const hasDemoData = useRiskPoolDemoData();
  
  // Get organizations - use demo organizations if demo data is enabled, otherwise use API data
  const { data: allOrganizationsData } = useRiskPoolOrganizations(currentOrganizationId || '', queryParams);
  const allOrganizations = useMemo(() => {
    if (hasDemoData) {
      return getPersistentOrganizations();
    }
    return allOrganizationsData?.pages
      ? allOrganizationsData.pages.flatMap((page) => page.organizations)
      : [];
  }, [hasDemoData, allOrganizationsData]);

  const handleOrgChange = (event: SelectChangeEvent<string>) => {
    const newOrgId = event.target.value;
    if (newOrgId) {
      const params = new URLSearchParams();
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (urlTimeRange) params.set('timeRange', urlTimeRange);
      const queryString = params.toString();
      navigate(`/admin/dashboard/risk_pool/${newOrgId}${queryString ? `?${queryString}` : ''}`);
    }
  };

  // Check demo data feature flags
  const hasPerformanceReportingDemo = useRiskPoolPerformanceReportingDemo();

  // Determine available tabs based on demo data feature flags
  const availableTabs = useMemo(() => {
    const tabs: TabItem[] = [];

    // Always show Policy tab (users don't have access to solutions API, so we assume policy is available)
    tabs.push({
      label: t('dashboard.riskPool.riskPoolOrg.tabs.policy') || 'Policy',
      value: 'policy',
    });

    // Performance Reporting tab only available if demo data is enabled
    if (hasPerformanceReportingDemo) {
      tabs.push({
        label: t('dashboard.riskPool.riskPoolOrg.tabs.performanceReporting') || 'Performance Reporting',
        value: 'performance-reporting',
      });
    }

    // Sort tabs alphabetically by label (ensuring label is a string)
    return tabs.sort((a, b) => {
      const labelA = typeof a.label === 'string' ? a.label : String(a.label);
      const labelB = typeof b.label === 'string' ? b.label : String(b.label);
      return labelA.localeCompare(labelB);
    });
  }, [hasPerformanceReportingDemo]);

  // Set default tab to first available tab
  const defaultTab = availableTabs.length > 0 ? availableTabs[0].value : 'policy';

  const { values, setters } = useUrlStates({
    tab: { defaultValue: defaultTab },
  });

  const { tab: currentTab } = values;
  const { tab: setTabRaw } = setters;
  
  // Type assertion to fix TypeScript inference issue
  const setTab = setTabRaw as (value: string | ((prev: string) => string)) => void;

  return (
    <RiskPoolOrgProvider
      orgId={orgId || ''}
      initialStartDate={startDate || undefined}
      initialEndDate={endDate || undefined}
      initialTimeRangeFilter={initialTimeRange}
    >
      <RiskPoolOrganizationDetailInner
        orgId={orgId || ''}
        organization={organization}
        isOrgLoading={isOrgLoading}
        allOrganizations={allOrganizations}
        onOrgChange={handleOrgChange}
        availableTabs={availableTabs}
        currentTab={String(currentTab)}
        setTabRaw={setTab}
        startDate={startDate}
        endDate={endDate}
        initialTimeRange={initialTimeRange}
        searchParams={searchParams}
      />
    </RiskPoolOrgProvider>
  );
}

function RiskPoolOrganizationDetailInner({
  orgId,
  organization,
  isOrgLoading,
  allOrganizations,
  onOrgChange,
  availableTabs,
  currentTab,
  setTabRaw,
  startDate,
  endDate,
  initialTimeRange,
  searchParams,
}: {
  orgId: string;
  organization: any;
  isOrgLoading: boolean;
  allOrganizations: Array<{ id: string; name: string }>;
  onOrgChange: (event: SelectChangeEvent<string>) => void;
  availableTabs: TabItem[];
  currentTab: string;
  setTabRaw: (value: string | ((prev: string) => string)) => void;
  startDate: string | null;
  endDate: string | null;
  initialTimeRange: TimeRangeFilter;
  searchParams: URLSearchParams;
}) {
  const { timeRangeFilter, setTimeRangeFilter } = useRiskPoolOrgContext();
  const [searchParamsState, setSearchParamsState] = useSearchParams();

  // Sync time range filter changes to URL
  useEffect(() => {
    const params = new URLSearchParams(searchParamsState);
    params.set('timeRange', timeRangeFilter);
    // Clear custom date parameters when using time range filter
    params.delete('startDate');
    params.delete('endDate');
    setSearchParamsState(params, { replace: true });
  }, [timeRangeFilter, searchParamsState, setSearchParamsState]);

  // When URL has custom dates but no timeRange, remove custom dates and use time range filter
  useEffect(() => {
    if (startDate && endDate && !searchParams.get('timeRange')) {
      const params = new URLSearchParams(searchParams);
      params.delete('startDate');
      params.delete('endDate');
      params.set('timeRange', initialTimeRange);
      setSearchParamsState(params, { replace: true });
    }
  }, [startDate, endDate, searchParams, initialTimeRange, setSearchParamsState]);

  const renderTabContent = () => {
    // If no tabs are available, show a message
    if (availableTabs.length === 0) {
      return (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          {t('dashboard.riskPool.riskPoolOrg.noAccess') ||
            'You do not have access to any reporting tabs for this organization.'}
        </Box>
      );
    }

    // If current tab is not in available tabs, default to first available
    const validTab = availableTabs.find((tab) => tab.value === currentTab)?.value || availableTabs[0].value;

    if (validTab === 'performance-reporting') {
      return (
        <PerformanceReportingProvider
          childOrgId={orgId}
          initialStartDate={startDate || undefined}
          initialEndDate={endDate || undefined}
          initialTimeRangeFilter={timeRangeFilter}
        >
          <PerformanceReporting />
        </PerformanceReportingProvider>
      );
    }

    return <Policy />;
  };

  if (isOrgLoading) {
    return <Loading />;
  }

  if (!organization) {
    const backUrl = `/admin/dashboard/risk_pool${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
    return (
      <TakeoverLayout
        pageTitle={t('dashboard.riskPool.riskPoolOrg.errorTitle')}
        backUrl={backUrl}
      >
        <Box sx={{ p: 4 }}>
          <Box sx={{ color: 'error.main' }}>{t('dashboard.riskPool.riskPoolOrg.organizationNotFound')}</Box>
        </Box>
      </TakeoverLayout>
    );
  }

  const backUrl = `/admin/dashboard/risk_pool${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;

  return (
    <TakeoverLayout
      pageTitle={organization.name}
      backUrl={backUrl}
      menubarLeft={
        <Filters
          allOrganizations={allOrganizations}
          onOrgChange={onOrgChange}
        />
      }
      contentHeaderLeft={
        availableTabs.length > 1 ? (
          <Tabs
            value={currentTab}
            onChange={(_event: React.SyntheticEvent, newValue: string | number) => {
              setTabRaw(String(newValue));
            }}
            items={availableTabs}
          />
        ) : undefined
      }
    >
      {renderTabContent()}
    </TakeoverLayout>
  );
}

export default function RiskPoolOrganizationDetail() {
  return <RiskPoolOrganizationDetailContent />;
}
